import type { CSSProperties, RefObject } from 'react';
import { Link } from 'react-router-dom';
import type { SkillLevelDto, SkillSessionDto, TrainsAtDto } from '../api/skills';
import { Aside } from '../components/Aside';
import { AnticipationRing } from '../components/AnticipationRing';
import { Dialog } from '../components/BottomSheet';
import { SkillBusyCard } from '../components/SkillBusyCard';
import { levelFraction } from '../components/SkillHeader';

export type SkillsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companionName: string;
  /** The server's S3 order — rendered as given, never re-sorted. */
  skills?: SkillLevelDto[];
  session?: SkillSessionDto;
  now: Date;
  /** Present while `HabitatScreen` can cancel; the card itself hides Cancel once `now >= completesAt`. */
  onCancel?: () => void;
  cancelPending?: boolean;
  /** The `Skills ›` button that opened the sheet — focus returns there on close. */
  triggerRef?: RefObject<HTMLElement | null>;
};

/**
 * Where a skill trains, as a route: the land pin (`/world/forage/:activityCode`) for a gather
 * skill, the bench (`/world/craft/:activityCode`) for a process one. `landCode` is the REAL land
 * code the server resolved (`tidewrack_cove`, not "Tidewrack") and rides `?land=` verbatim so the
 * destination's back link returns to the land, not the world hub.
 */
function hrefFor(trainsAt: TrainsAtDto): string {
  const base = trainsAt.kind === 'process' ? '/world/craft' : '/world/forage';
  return `${base}/${trainsAt.activityCode}?land=${trainsAt.landCode}`;
}

/**
 * THE SKILLS SHEET (skill-craft UI polish, R2 / spec §3.8) — the roster the Habitat's one-line
 * `SkillsLine` opens. A bottom sheet on the phone, the Studio's right-slot panel on the desk
 * (`Aside`). At the top, when a session runs, the full busy card with its place and Cancel — the
 * only place the Habitat offers that verb. Then eight rows in the server's S3 order: the level
 * ring at `xp / xpToNext` (`levelFraction`) with the level inside (the same `.skill-ring` idiom as
 * `SkillHeader`), the name, `xp / xpToNext xp`, an XP bar at the same fraction, and a link to
 * where the skill trains today. Habitat stays read-only for Start (R2): the link goes to the pin
 * or bench, which owns the verb.
 */
export function SkillsSheet({
  open, onOpenChange, companionName, skills, session, now, onCancel, cancelPending, triggerRef,
}: SkillsSheetProps) {
  const title = `${companionName}'s skills`;
  return (
    <Aside
      title={title}
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="skills-sheet-content"
      restoreFocusRef={triggerRef}
    >
      <div className="sheet-header skills-sheet-header">
        <Dialog.Title asChild><h2>{title}</h2></Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close">Close</button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Every skill&rsquo;s level and where it trains.
      </Dialog.Description>

      <div className="sheet-body">
        {session && (
          <SkillBusyCard session={session} now={now} onCancel={onCancel} cancelPending={cancelPending} showPlace />
        )}
        <ul className="skills-sheet">
          {(skills ?? []).map((s) => {
            const fraction = levelFraction(s);
            const pct = `${(fraction * 100).toFixed(1)}%`;
            return (
              <li key={s.code} className="skills-sheet-row">
                <div className="skill-ring">
                  <AnticipationRing fraction={fraction} size="sm" />
                  <span className="skill-ring-inner">{s.level}</span>
                </div>
                <div className="skills-sheet-copy">
                  <p className="skills-sheet-name">{s.name}</p>
                  <p className="skills-sheet-meta">{`${s.xp} / ${s.xpToNext} xp`}</p>
                  <div className="skills-sheet-xp" aria-hidden="true">
                    <i style={{ '--w': pct } as CSSProperties} />
                  </div>
                </div>
                <Link
                  className="skills-sheet-link"
                  to={hrefFor(s.trainsAt)}
                  onClick={() => onOpenChange(false)}
                >
                  {`${s.trainsAt.activityName} ›`}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </Aside>
  );
}
